import { Placings } from '../../components/Placings'
import { Link, useParams } from 'react-router-dom'
import { api, type Projection as Proj, type ProjectedMatch } from '../../api/public'
import { Empty, Failed } from '../../components/Async'
import { LiveLoading, LiveStatus } from '../../components/Live'
import { nextPoll } from '../../components/eventState'
import { usePolling } from '../../hooks/usePolling'
import { usePlayerPaths } from '../../components/playerPaths'

/**
 * Where the season stands if play stopped now — committing none of it
 * (FR-061).
 *
 * # It must never be mistakable for the standings (FR-063, SC-023)
 *
 * That is the whole design problem of this view, and it is answered in four
 * places rather than one, because a single "provisional" label is one CSS
 * change away from disappearing:
 *
 *   - it is a **separate route**, on its own URL, reached deliberately;
 *   - every number is in a **projected** column beside the committed one, so
 *     the two are never the same figure with a different meaning;
 *   - the page is visibly marked throughout, including a banner that cannot be
 *     scrolled past before the table;
 *   - it says **what it is waiting for** — the holes it compared, and anything
 *     it could not decide.
 *
 * # "Through 6" is the honest form of the claim
 *
 * The low net here is computed over the holes EVERY player has completed, and
 * `through_holes` says which. Ranking a partial field on total net would put
 * whoever has played fewest holes on top, because they have simply added up
 * less golf. Where no hole is common to everybody there is no basis to compare
 * at all, and the server says so rather than naming a leader — which this view
 * renders as an absence rather than papering over.
 *
 * # A projection cannot disagree with completing
 *
 * The server derives it with the same code Complete Round uses, so once every
 * player has a full card this IS what completing would record. The difference
 * between a projection and a result is WHEN, not WHAT.
 */
export function Projection() {
  const { pathFor } = usePlayerPaths()
  const { eventId } = useParams()
  const id = Number(eventId)
  // Nothing to project once the week is closed: the round is a record and the
  // page says so. Before this it kept asking anyway.
  const live = usePolling(
    (s) => api.projection(id, s),
    [id],
    (d: Proj) => nextPoll(d.event.state),
  )

  if (live.loading) return <LiveLoading what="the projection" />
  if (!live.data) {
    return <Failed what="The projection" error={live.error ?? new Error('nothing loaded')} />
  }

  const proj: Proj = live.data
  const through = proj.event.through_holes

  return (
    <section className="projection" data-testid="projection">
      <h2>
        Projected standings <span className="projection-tag">not official</span>
      </h2>

      <p className="projection-banner" role="status" data-testid="projection-banner">
        <strong>This is a projection, not the standings.</strong>{' '}
        {proj.event.recorded ? (
          <>
            This round has been completed, so the night itself is recorded — see its{' '}
            <Link to={`/events/${proj.event.event_id}`}>results</Link>. The table below is the
            season as it stands with every round still in progress treated as if it ended now.
          </>
        ) : (
          <>
            It is what the season would look like if play stopped right now. Nothing here has been
            awarded, and none of it is recorded until the round is completed.
          </>
        )}{' '}
        <Link to="/standings">The official standings are here.</Link>
      </p>

      <p className="note">
        {/* One link, because the scoreboard and the scorecards are now one
            page. These were two, and after the consolidation they both pointed
            here. */}
        <Link to={`/events/${proj.event.event_id}`}>This week</Link> ·{' '}
        <Link to={`/events/${proj.event.event_id}/tracker`}>Round tracker</Link>
      </p>

      <LiveStatus loadedAt={live.loadedAt} failures={live.failures} error={live.error} />

      <p className="note note-provisional" data-testid="through-holes">
        {proj.event.recorded ? (
          <>This round is complete. Tonight&rsquo;s results below are what it recorded.</>
        ) : proj.event.complete ? (
          <>
            Every player has a full card, so this is exactly what completing the round would record
            — it simply has not been completed yet.
          </>
        ) : through.length === 0 ? (
          <>
            No hole has been completed by everybody yet, so there is nothing to compare the field
            on. Match projections below are still meaningful; the low net is not.
          </>
        ) : (
          <>
            Compared <strong>through {through.length} holes</strong> — the {through.length} every
            player has finished ({through.join(', ')}). A player who has played further is not
            counted ahead for it.
          </>
        )}
      </p>

      {proj.event.undetermined.length > 0 && (
        <ul className="note note-warn undetermined" data-testid="undetermined">
          {proj.event.undetermined.map((u) => (
            <li key={u}>{u}</li>
          ))}
        </ul>
      )}

      <h3>Tonight&rsquo;s matches</h3>
      {proj.event.matches.length === 0 ? (
        <Empty>This event has no pairings — it is played as a field.</Empty>
      ) : (
        <ul className="match-list">
          {proj.event.matches.map((m) => (
            <li
              key={m.match_id}
              className="match-block"
              data-testid={`projected-match-${m.match_id}`}
            >
              <div className="match-line">
                <span>
                  {m.player_a_name} v {m.player_b_name}
                </span>
                <span className="match-outcome">
                  {m.status_text}
                  {m.already_settled ? (
                    /* Not a projection at all: no remaining hole can change it
                       (FR-030), or the commissioner recorded it (FR-031). */
                    <em
                      className="mark mark-settled"
                      title="Decided — no remaining hole can change it"
                    >
                      {' '}
                      decided
                    </em>
                  ) : (
                    <em className="mark mark-projected"> if it ended now: {ifItEndedNow(m)}</em>
                  )}
                </span>
              </div>
              {!m.already_settled && m.decided_by && (
                <p className="note">
                  Level on holes with {m.holes_remaining} to play, so this goes to the
                  league&rsquo;s tie-break: <strong>{m.decided_by.replace(/_/g, ' ')}</strong>. Any
                  of the remaining holes changes it.
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      <h3>What tonight would be worth</h3>
      {proj.event.placings.length === 0 ? (
        <Empty>Nothing can be projected from this event yet.</Empty>
      ) : (
        <Placings rows={proj.event.placings} testId="projected-results" />
      )}

      <h3>The season, if it ended now</h3>
      <div className="scroll-x">
        <table className="table projection-table" data-testid="projection-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Player</th>
              <th scope="col" className="numeric">
                Official
              </th>
              {/* The change to the season total, after the season's best-N rule.
                  A fourteenth round under a best-13 cap adds less than it
                  earned, so this is not the night's points (those are above). */}
              <th scope="col" className="numeric" title="Change to the season total">
                Change
              </th>
              <th scope="col" className="numeric">
                Would be
              </th>
              {proj.has_matches && <th scope="col">Record</th>}
            </tr>
          </thead>
          <tbody>
            {proj.rows.map((row) => (
              <tr key={row.player_id} data-testid={`projected-row-${row.player_id}`}>
                <td className="numeric">{row.projected_position}</td>
                <th scope="row">
                  <Link to={pathFor(row.player_id)}>{row.display_name}</Link>
                  {row.committed_position > 0 &&
                    row.committed_position !== row.projected_position && (
                      <em className="mark mark-move">
                        {' '}
                        {row.projected_position < row.committed_position ? '▲' : '▼'} from{' '}
                        {row.committed_position}
                      </em>
                    )}
                </th>
                {/* Committed and projected are never the same column. Decimal
                    strings throughout — the server does the arithmetic. */}
                <td className="numeric">{row.committed_points}</td>
                <td className="numeric projected-points">{signed(row.projected_points)}</td>
                <td className="numeric strong projected-total">{row.projected_total}</td>
                {proj.has_matches && (
                  <td className="numeric">
                    {row.matches_won}–{row.matches_lost}
                    {row.matches_halved > 0 && `–${row.matches_halved}`}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {proj.rows.some((r) => !isZero(r.projected_points)) && (
        <p className="note">
          <strong>Change</strong> is what tonight adds to each season total. Where a season counts
          only a player&rsquo;s best rounds, a night can add less than it is worth, because a weaker
          round drops out to make room for it.
        </p>
      )}

      <p className="note">
        {proj.committed_through === null ? (
          <>
            No event of this season has been committed yet, so the &ldquo;official&rdquo; column is
            zero for everybody — every point below is provisional.
          </>
        ) : (
          <>
            Official points are committed through{' '}
            <Link to={`/events/${proj.committed_through}`}>event {proj.committed_through}</Link>.
            Everything beyond it is provisional.
          </>
        )}
        {proj.in_progress.length > 0 && (
          <>
            {' '}
            {proj.in_progress.length === 1
              ? 'One event is'
              : `${proj.in_progress.length} events are`}{' '}
            in progress:{' '}
            {proj.in_progress.map((e, i) => (
              <span key={e.id}>
                {i > 0 && ', '}
                <Link to={`/events/${e.id}`}>week {e.week_number}</Link>
              </span>
            ))}
            .
          </>
        )}
      </p>
    </section>
  )
}

/** Who the match goes to as it stands — never phrased as a result. */
/**
 * The change, signed — **without parsing it**.
 *
 * The "+" used to be hardcoded, which was right while a night could only ADD to
 * a season total. A re-score can now REVOKE a published award (FR-067), so the
 * change goes negative and the cell read `+-1`.
 *
 * Every money-like value crosses the wire as a decimal STRING and must stay one
 * (Constitution III): parsing this into a JavaScript number to test its sign
 * would reintroduce, in the browser, exactly the error the scoring core exists
 * to avoid. So the sign is read off the first character and the digits are
 * never touched.
 */
function signed(points: string): string {
  if (points.startsWith('-') || isZero(points)) return points
  return `+${points}`
}

/** Zero, however many decimal places the server chose to send. */
function isZero(points: string): boolean {
  return /^[+-]?0(\.0+)?$/.test(points)
}

function ifItEndedNow(m: ProjectedMatch): string {
  if (m.halved) return 'halved'
  if (!m.winner_player_id) return 'undecided'
  return m.winner_player_id === m.player_a_id ? m.player_a_name : m.player_b_name
}
